import React, { useEffect, useState } from "react";
import integrityLogo from "../assets/integrity-lifeline-logo.png";
import { getCurrentRole } from "../api";
import { getCompanyBranding } from "../companyBrandingApi";
import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

const DEFAULT_PRIMARY = "#0B0B0B";
const DEFAULT_SECONDARY = "#D4AF37";

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const role = getCurrentRole();

  const isAdmin = role === "admin";
  const isManager = role === "manager";
  const isCredit = role === "credit_specialist";

  const [branding, setBranding] = useState({
    name: "Integrity Suite CRM",
    logo_url: "",
    primary_color: DEFAULT_PRIMARY,
    secondary_color: DEFAULT_SECONDARY,
  });

  useEffect(() => {
    let active = true;

    async function loadBranding() {
      try {
        const data = await getCompanyBranding();

        if (!active || !data) {
          return;
        }

        setBranding({
          name:
            data.name ||
            "Integrity Suite CRM",
          logo_url: data.logo_url || "",
          primary_color:
            data.primary_color ||
            DEFAULT_PRIMARY,
          secondary_color:
            data.secondary_color ||
            DEFAULT_SECONDARY,
        });
      } catch {
        // Keep Integrity Suite defaults when no company branding exists.
      }
    }

    function handleBrandingUpdate(event) {
      const data = event?.detail;

      if (!data) {
        return;
      }

      setBranding({
        name:
          data.name ||
          "Integrity Suite CRM",
        logo_url: data.logo_url || "",
        primary_color:
          data.primary_color ||
          DEFAULT_PRIMARY,
        secondary_color:
          data.secondary_color ||
          DEFAULT_SECONDARY,
      });
    }

    loadBranding();

    window.addEventListener(
      "company-branding-updated",
      handleBrandingUpdate
    );

    return () => {
      active = false;

      window.removeEventListener(
        "company-branding-updated",
        handleBrandingUpdate
      );
    };
  }, []);

  function handleLogout() {
    localStorage.removeItem("token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");

    navigate("/login", { replace: true });
  }

  const primaryColor =
    branding.primary_color || DEFAULT_PRIMARY;

  const secondaryColor =
    branding.secondary_color || DEFAULT_SECONDARY;

  const baseLinkStyle = {
    color: "white",
    textDecoration: "none",
    fontSize: "18px",
    padding: "12px 16px",
    borderRadius: "12px",
    display: "block",
    marginBottom: "10px",
  };

  function getLinkStyle(path) {
    const active =
      location.pathname === path;

    return {
      ...baseLinkStyle,
      background: active
        ? "rgba(255,255,255,0.08)"
        : "transparent",
      color: active
        ? secondaryColor
        : "#FFFFFF",
      border: active
        ? `1px solid ${secondaryColor}`
        : "1px solid transparent",
    };
  }

  const usingCustomLogo = Boolean(
    branding.logo_url
  );

  return (
    <div
      style={{
        width: "280px",
        background: primaryColor,
        color: "white",
        padding: "24px 18px",
        boxSizing: "border-box",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <div>
        <div
          style={{
            marginBottom: "14px",
            display: "flex",
            justifyContent: "left",
          }}
        >
          <img
            src={
              usingCustomLogo
                ? branding.logo_url
                : integrityLogo
            }
            alt={
              branding.name ||
              "Integrity Suite"
            }
            style={{
              width: "100%",
              maxWidth: "220px",
              maxHeight: "110px",
              height: "auto",
              objectFit: "contain",
              objectPosition: "left center",
              display: "block",
            }}
          />
        </div>

        <div
          style={{
            textAlign: "left",
            marginBottom: "14px",
            maxWidth: "220px",
          }}
        >
          <div
            style={{
              fontSize: "22px",
              fontWeight: "900",
              color: secondaryColor,
              letterSpacing: "0.4px",
              lineHeight: 1.1,
            }}
          >
            {branding.name ||
              "Integrity Suite CRM"}
          </div>

          <div
            style={{
              fontSize: "12px",
              color: "#f3ead0",
              marginTop: "4px",
              letterSpacing: "0.35px",
            }}
          >
            Credit Repair Management Platform
          </div>
        </div>

        <div
          style={{
            color: "#D8C98F",
            fontSize: "16px",
            lineHeight: 1.45,
            marginBottom: "20px",
            maxWidth: "220px",
          }}
        >
          Manage clients, letters, disputes, tasks,
          documents, credit reports, and reports.
        </div>

        <div
          style={{
            height: "1px",
            background:
              "rgba(255,255,255,0.08)",
            marginBottom: "18px",
          }}
        />

        <nav style={{ flex: 1 }}>
          <Link
            to="/dashboard"
            style={getLinkStyle("/dashboard")}
          >
            Dashboard
          </Link>

          <Link
            to="/clients"
            style={getLinkStyle("/clients")}
          >
            Clients
          </Link>

          {isAdmin || isManager ? (
            <Link
              to="/employees"
              style={getLinkStyle("/employees")}
            >
              Employees
            </Link>
          ) : null}

          <Link
            to="/letters"
            style={getLinkStyle("/letters")}
          >
            Letters
          </Link>

          <Link
            to="/disputes"
            style={getLinkStyle("/disputes")}
          >
            Disputes
          </Link>

          <Link
            to="/notes-tasks"
            style={getLinkStyle("/notes-tasks")}
          >
            Notes / Tasks
          </Link>

          <Link
            to="/documents"
            style={getLinkStyle("/documents")}
          >
            Documents
          </Link>

          {isAdmin || isCredit ? (
            <Link
              to="/credit-reports"
              style={getLinkStyle(
                "/credit-reports"
              )}
            >
              Credit Reports
            </Link>
          ) : null}

          {isAdmin || isCredit ? (
            <Link
              to="/case-files"
              style={getLinkStyle(
                "/case-files"
              )}
            >
              📂 Case Files
            </Link>
          ) : null}

          {isAdmin || isManager ? (
            <Link
              to="/reports"
              style={getLinkStyle("/reports")}
            >
              Reports
            </Link>
          ) : null}

          {isAdmin || isManager ? (
            <Link
              to="/settings"
              style={getLinkStyle("/settings")}
            >
              Settings
            </Link>
          ) : null}
        </nav>
      </div>

      <div>
        <div
          style={{
            marginTop: "28px",
            background:
              "rgba(255,255,255,0.04)",
            border:
              "1px solid rgba(255,255,255,0.07)",
            borderRadius: "22px",
            padding: "20px",
            color: "#d6e4ff",
            lineHeight: 1.5,
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: "800",
              letterSpacing: "1.8px",
              color: secondaryColor,
              marginBottom: "10px",
            }}
          >
            WORKFLOW STATUS
          </div>

          <div
            style={{
              fontSize: "16px",
              color: "#f3ead0",
            }}
          >
            Your CRM includes client management,
            dispute workflows, reporting, documents,
            and credit report imports.
          </div>
        </div>

        <button
          onClick={handleLogout}
          style={{
            marginTop: "18px",
            background: "#ef2b2b",
            color: "white",
            border: "none",
            borderRadius: "16px",
            padding: "16px",
            fontSize: "18px",
            fontWeight: "700",
            cursor: "pointer",
            width: "100%",
          }}
        >
          Logout
        </button>
      </div>
    </div>
  );
}
